import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';

import ActionsVideoThumbnailNormalXsmall from '@strava/icons/ActionsVideoThumbnailNormalXsmall';
import ActionsPauseNormalXsmall from '@strava/icons/ActionsPauseNormalXsmall';
import ActionsAudioOffNormalXsmall from '@strava/icons/ActionsAudioOffNormalXsmall';
import ActionsAudioOnNormalXsmall from '@strava/icons/ActionsAudioOnNormalXsmall';

import { TOGGLE_IS_ACTIVITY_VIDEO_MUTED } from '../../../../feed.actions';
import { useFeedContext } from '../../../../FeedContext';
import { useFeedEntityContext } from '../../../../FeedEntityContext';

import styles from './Controls.scss';

function Controls({ duration, video, isAutoplayEnabled, videoId }) {
  const {
    entityState: { isActivityVideoMuted },
    dispatch: dispatchFeed
  } = useFeedContext();

  const [state, setState] = useState({
    time: duration,
    isPlaying: false
  });

  const { feedEntityState } = useFeedEntityContext();

  function handleMuteToggleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const muted = !video.muted();
    video.muted(muted);

    dispatchFeed({
      type: TOGGLE_IS_ACTIVITY_VIDEO_MUTED,
      payload: { muted }
    });

    feedEntityState.trackAnalytics({
      category: 'feed',
      page: 'activity',
      action: 'click',
      element: 'video_audio',
      properties: {
        video_id: videoId,
        audio_cta: video.muted() ? 'muted' : 'unmuted'
      }
    });
  }

  function handlePlayEvent() {
    setState((prevState) => ({
      ...prevState,
      isPlaying: true,
      time: video.remainingTime()
    }));
  }

  function handlePauseEvent() {
    setState((prevState) => ({
      ...prevState,
      isPlaying: false
    }));
  }

  function handleTimeupdate() {
    setState((prevState) => ({
      ...prevState,
      time: video.remainingTime()
    }));
  }

  function handlePlayToggleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (state.isPlaying) {
      video.pause();
    } else {
      // Pause all video that are currently playing
      videojs
        .getAllPlayers()
        .filter((p) => !p.paused())
        .forEach((p) => p.pause());

      video.play();
    }
  }

  useEffect(() => {
    if (video) {
      video.on('play', handlePlayEvent);
      video.on('pause', handlePauseEvent);
      video.on('timeupdate', handleTimeupdate);
    }

    return () => {
      if (video) {
        video.off('play', handlePlayEvent);
        video.off('pause', handlePauseEvent);
        video.off('timeupdate', handleTimeupdate);
      }
    };
  }, [video]);

  if (!video) {
    return null;
  }

  return (
    <>
      {/* Timer */}
      {isAutoplayEnabled || state.isPlaying ? (
        <div role="timer" className={styles.time}>
          {videojs.formatTime(state.time)}
        </div>
      ) : null}

      {/* Play/Stop button */}
      {!isAutoplayEnabled ? (
        <div
          role="button"
          className={styles.playControl}
          onClick={handlePlayToggleClick}
          onKeyPress={handlePlayToggleClick}
          tabIndex="0"
          data-testid="play-toggle"
        >
          {state.isPlaying ? (
            <ActionsPauseNormalXsmall data-testid="pause-icon" />
          ) : (
            <ActionsVideoThumbnailNormalXsmall data-testid="play-icon" />
          )}
        </div>
      ) : null}

      {/* Mute/Unmute button */}
      {state.isPlaying ? (
        <div
          role="button"
          className={styles.muteControl}
          onClick={handleMuteToggleClick}
          onKeyPress={handleMuteToggleClick}
          tabIndex="0"
          data-testid="mute-toggle"
        >
          {isActivityVideoMuted ? (
            <ActionsAudioOffNormalXsmall data-testid="mute-icon" />
          ) : (
            <ActionsAudioOnNormalXsmall data-testid="unmute-icon" />
          )}
        </div>
      ) : null}
    </>
  );
}

Controls.defaultProps = {
  video: null,
  isAutoplayEnabled: true
};

Controls.propTypes = {
  duration: PropTypes.number.isRequired,
  video: PropTypes.shape({
    on: PropTypes.func.isRequired,
    one: PropTypes.func.isRequired,
    off: PropTypes.func.isRequired,
    readyState: PropTypes.func.isRequired,
    play: PropTypes.func.isRequired,
    pause: PropTypes.func.isRequired,
    paused: PropTypes.func.isRequired,
    duration: PropTypes.func.isRequired,
    remainingTime: PropTypes.func.isRequired,
    muted: PropTypes.func.isRequired
  }),
  isAutoplayEnabled: PropTypes.bool,
  videoId: PropTypes.string.isRequired
};

export default Controls;
